import { useMemo } from "react";
import QRCode from "./QrCode";

// Each <Page> is a single A4 printed page. Everything brand-specific — name,
// accent colour, footer, which pages appear — comes from the template, so one
// component prints eMedHub, TrustEdge CRM, HRZyra or eMedAyush quotations.
const fmt = (v) => {
  const n = Number(v || 0);
  return n ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00";
};
const brandOf = (t) => (t?.brand_name || "").trim() || "eMedHub";

// Figures and bank details stay exactly as saved, even in edit mode: a
// quotation whose printed total differs from the recorded one is worse than
// no quotation. Wrap them in this.
const Locked = ({ children, className = "" }) => (
  <div contentEditable={false} className={`locked ${className}`}>{children}</div>
);

// One quotation, the whole document.
export default function QuotationDocument({ quote, template, modules, editable = false }) {
  return (
    <DocShell editable={editable}>
      <CoverPage quote={quote} template={template} />
      <IntroPages template={template} modules={modules} />
      <PricingPage quote={quote} template={template} />
      <ClosingPages template={template} />
    </DocShell>
  );
}

// Several quotations for one lead as one document: one cover, a summary of
// what each product costs and the combined total, then each product's pricing
// in its own branding, and the terms and payment pages once, at the end.
export function MergedQuotationDocument({ merged, modules, editable = false }) {
  const lead = merged.lead_template || {};
  const coverQuote = {
    customer_name: merged.customer_name,
    customer_city: merged.customer_city,
    status: null,
  };
  const brands = [...new Set(merged.sections.map(s => brandOf(s.template)))];
  return (
    <DocShell editable={editable}>
      <CoverPage quote={coverQuote} template={lead}
                 title={lead.cover_title}
                 subtitle={`Proposal covering ${brands.join(", ")}`} />
      <SummaryPage merged={merged} template={lead} />
      {merged.sections.map(s => (
        <PricingPage key={s.quotation.id} quote={s.quotation} template={s.template} showNumber />
      ))}
      <ClosingPages template={lead} />
    </DocShell>
  );
}

function DocShell({ editable, children }) {
  return (
    <div className={`quotation-doc ${editable ? "is-editing" : ""}`}
         contentEditable={editable} suppressContentEditableWarning spellCheck={false}>
      <style>{PRINT_STYLES}</style>
      {children}
    </div>
  );
}

function CoverPage({ quote, template, title, subtitle }) {
  const brand = brandOf(template);
  const statusWatermark = {
    draft: "DRAFT", accepted: "ACCEPTED", rejected: "REJECTED", expired: "EXPIRED",
  }[quote.status];
  const approvalStamp = (() => {
    if (quote.approval_status === "pending")  return { label: "PENDING APPROVAL", cls: "stamp-pending" };
    if (quote.approval_status === "approved") {
      const who = quote.approved_by_name ? ` by ${quote.approved_by_name}` : "";
      return { label: `APPROVED${who}`, cls: "stamp-approved" };
    }
    if (quote.approval_status === "rejected") return { label: "APPROVAL REJECTED", cls: "stamp-rejected" };
    return null;
  })();
  const pills = (template.certifications || []).map(c => c.name).filter(Boolean).slice(0, 4);

  return (
    <Page template={template} watermark={statusWatermark}>
      <div className="cover">
        <div className="cover-header">
          <div className="logo-block">
            <div className="logo-dot">{brand.charAt(0).toUpperCase()}</div>
            <div className="logo-text">{brand}</div>
          </div>
          <div className="tn-logo">TecnoSpice</div>
        </div>
        {approvalStamp && (
          <div className={`approval-stamp ${approvalStamp.cls}`}>{approvalStamp.label}</div>
        )}
        <h1 className="cover-title">{title || template.cover_title}</h1>
        <p className="cover-subtitle">{subtitle || template.cover_subtitle}</p>
        {pills.length > 0 && (
          <div className="cert-row">
            {pills.map(p => <span key={p} className="cert-pill">{p}</span>)}
          </div>
        )}
        <div className="cover-footer">
          <div>
            <p className="lbl">Prepared For</p>
            <h3>{quote.customer_name || "—"}</h3>
            {quote.customer_city && <p className="muted">{quote.customer_city}</p>}
          </div>
          <div>
            <p className="lbl">Prepared By</p>
            <h3>{template.company_name || "—"}</h3>
            <p className="muted">{template.company_address?.split(",").slice(-2).join(",").trim() || ""}</p>
          </div>
        </div>
      </div>
    </Page>
  );
}

// Intro, what-we-do, key features and the product catalogue. A page with no
// content is left out rather than printed blank — a CRM template has no
// hospital module grid, and an empty "What we do" page reads as a mistake.
function IntroPages({ template, modules }) {
  const brand = brandOf(template);
  const hasIntro = template.intro_text || (template.why_choose_us || []).length || (template.certifications || []).length;
  const products = (modules || []).filter(m => m.charge_type === "included_only");
  return (
    <>
      {hasIntro && (
        <Page template={template}>
          <SectionHeader>Introduction</SectionHeader>
          {template.intro_text && <p className="body-text pre-line">{template.intro_text}</p>}
          {(template.founder_name || template.founder_org) && (
            <p className="signature"><b>{template.founder_name}</b><br/><i>{template.founder_org}</i></p>
          )}
          {((template.why_choose_us || []).length > 0 || (template.certifications || []).length > 0) && (
            <>
              <h2 className="h2">Why choose us</h2>
              <div className="two-col">
                <div className="why-col">
                  {(template.why_choose_us || []).map((w, i) => (
                    <div key={i} className="why-item">
                      <div className="why-dot" />
                      <div>
                        <p className="why-title">{w.title}</p>
                        <p className="why-desc">{w.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {(template.certifications || []).length > 0 && (
                  <div className="cert-col">
                    <p className="cert-head">OUR CERTIFICATIONS</p>
                    {template.certifications.map((c, i) => (
                      <div key={i} className="cert-item">
                        <div className="cert-icon">✓</div>
                        <div>
                          <p className="cert-name">{c.name}</p>
                          <p className="cert-desc">{c.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </Page>
      )}

      {(template.what_we_do || []).length > 0 && (
        <Page template={template}>
          <SectionHeader>What {brand} Does For You</SectionHeader>
          <div className="what-grid">
            {template.what_we_do.map((w, i) => (
              <div key={i} className="what-card">
                <h4>{w.title}</h4>
                <p>{w.description}</p>
              </div>
            ))}
          </div>
        </Page>
      )}

      {(template.key_features || []).length > 0 && (
        <Page template={template}>
          <SectionHeader>Key Features &amp; Advantages of {brand}</SectionHeader>
          <ul className="key-list">
            {template.key_features.map((k, i) => (<li key={i}>{k}</li>))}
          </ul>
        </Page>
      )}

      {template.show_products_page !== false && products.length > 0 && (
        <Page template={template}>
          <SectionHeader>Products of {brand}</SectionHeader>
          <p className="muted small">The full catalog of modules available.</p>
          <div className="prod-grid">
            {products.map(m => (
              <div key={m.id} className="prod-card">
                <div className="prod-ico">✚</div>
                <span>{m.name}</span>
              </div>
            ))}
          </div>
        </Page>
      )}
    </>
  );
}

function PricingPage({ quote, template, showNumber = false }) {
  const brand = brandOf(template);
  return (
    <Page template={template}>
      <SectionHeader>Our Pricing Plan{showNumber ? ` — ${brand}` : ""}</SectionHeader>
      {showNumber && (
        <p className="muted small" style={{ marginTop: -10, marginBottom: 10 }}>
          {quote.quote_number}{quote.valid_until ? ` · valid until ${quote.valid_until}` : ""}
        </p>
      )}
      <p className="body-text">
        At {brand}, we provide a variety of flexible pricing structures designed to align with
        your unique operational requirements.
      </p>
      <div className={`plans-row plans-${quote.plans.length}`}>
        {quote.plans.map(plan => <PlanCard key={plan.id} plan={plan} />)}
      </div>

      {template.other_charges_text && (
        <div className="other-charges">
          <p className="other-head">Other Charges</p>
          <p className="body-text pre-line small">{template.other_charges_text}</p>
        </div>
      )}
    </Page>
  );
}

function SummaryPage({ merged, template }) {
  const basisNote = { accepted: "accepted plan", recommended: "recommended plan", first: "first plan", none: "no plan" };
  return (
    <Page template={template}>
      <SectionHeader>Proposal Summary</SectionHeader>
      <p className="body-text">
        This proposal brings together {merged.summary.length} quotations for {merged.customer_name}.
        Each product's full pricing follows on its own page.
      </p>
      <Locked>
        <table className="sum-table">
          <thead>
            <tr><th>Product</th><th>Quotation</th><th>Plan</th>
                <th className="num">Net</th><th className="num">GST</th><th className="num">Total</th></tr>
          </thead>
          <tbody>
            {merged.summary.map(r => (
              <tr key={r.quotation_id}>
                <td><b>{r.project_name || "—"}</b>{r.package_title && <div className="muted small">{r.package_title}</div>}</td>
                <td>{r.quote_number}</td>
                <td>{r.plan_label}<div className="muted small">{basisNote[r.basis]}</div></td>
                <td className="num">₹{fmt(r.net_amount)}</td>
                <td className="num">₹{fmt(r.gst_amount)}</td>
                <td className="num">₹{fmt(r.grand_total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}>Combined Total</td>
              <td className="num">₹{fmt(merged.totals.net_amount)}</td>
              <td className="num">₹{fmt(merged.totals.gst_amount)}</td>
              <td className="num">₹{fmt(merged.totals.grand_total)}</td>
            </tr>
          </tfoot>
        </table>
      </Locked>
      <p className="muted small" style={{ marginTop: 10 }}>
        Where a quotation offers several plans, the total counts the one accepted or recommended — not every alternative.
      </p>
    </Page>
  );
}

function ClosingPages({ template }) {
  const upiUrl = useMemo(() => {
    const pa = template.upi_id || "";
    const pn = template.upi_payee_name || template.bank_holder_name || "";
    if (!pa) return null;
    return `upi://pay?pa=${encodeURIComponent(pa)}&pn=${encodeURIComponent(pn)}&cu=INR`;
  }, [template.upi_id, template.upi_payee_name, template.bank_holder_name]);

  const contactEmails = (template.company_emails || "").split(",").map(s => s.trim()).filter(Boolean);
  const contactPhones = (template.company_phones || "").split(",").map(s => s.trim()).filter(Boolean);

  return (
    <>
      {(template.terms_text || template.implementation_text) && (
        <Page template={template}>
          {template.terms_text && (<>
            <SectionHeader>Terms &amp; Conditions</SectionHeader>
            <p className="body-text pre-line">{template.terms_text}</p>
          </>)}
          {template.implementation_text && (<>
            <h2 className="h2">Implementation &amp; Support Guidelines</h2>
            <p className="body-text pre-line">{template.implementation_text}</p>
          </>)}
        </Page>
      )}

      {(template.amc_text || template.upgrades_text) && (
        <Page template={template}>
          {template.amc_text && (<>
            <SectionHeader>Annual Maintenance Contract (AMC) covers</SectionHeader>
            <p className="body-text pre-line">{template.amc_text}</p>
          </>)}
          {template.upgrades_text && (<>
            <h2 className="h2">Software Upgrades &amp; Enhancements</h2>
            <p className="body-text pre-line">{template.upgrades_text}</p>
          </>)}
        </Page>
      )}

      <Page template={template}>
        <SectionHeader>Payment Details</SectionHeader>
        <p className="warn">
          Make payment only in the below given options and do not make payment to our staff
          personal bank account or personal UPI ID. We do not take responsibility for the
          payments made in any other bank accounts.
        </p>
        <Locked className="pay-row">
          <div className="pay-box">
            <p className="pay-head">Bank Transfer</p>
            <ul className="pay-list">
              <li><b>A/c Holder:</b> {template.bank_holder_name}</li>
              <li><b>Bank:</b> {template.bank_name}</li>
              <li><b>A/c No.:</b> {template.bank_account_no}</li>
              <li><b>Branch:</b> {template.bank_branch}</li>
              <li><b>IFSC:</b> {template.bank_ifsc}</li>
            </ul>
          </div>
          <div className="pay-box qr-box">
            <p className="pay-head">UPI Payments</p>
            {upiUrl ? (
              <>
                <QRCode value={upiUrl} size={150} />
                <p className="small muted" style={{ marginTop: 6 }}>{template.upi_id}</p>
              </>
            ) : (
              <p className="muted small">UPI ID not configured</p>
            )}
          </div>
        </Locked>

        <h2 className="h2" style={{ marginTop: 32 }}>Contact Details</h2>
        <div className="body-text">
          <p><b>{template.company_name}</b></p>
          <p>{template.company_address}</p>
          {template.company_website && <p>Website: {template.company_website}</p>}
          {contactEmails.length > 0 && <p>Email: {contactEmails.join(" & ")}</p>}
          {contactPhones.length > 0 && <p>Mobile: {contactPhones.join(" | ")}</p>}
        </div>
      </Page>
    </>
  );
}

function Page({ children, watermark, template }) {
  // The accent is set per page, so a merged document can carry each product's
  // own colour on its own pricing page.
  const style = { "--accent": template?.accent_color || "#0f766e" };
  return (
    <div className="page" style={style}>
      {watermark && <div className={`watermark wm-${watermark.toLowerCase()}`}>{watermark}</div>}
      <div className="page-inner">{children}</div>
      <div className="page-footer">
        {template?.footer_phone   && <span>📞 {template.footer_phone}</span>}
        {template?.footer_email   && <span>✉ {template.footer_email}</span>}
        {template?.footer_website && <span>🌐 {template.footer_website}</span>}
      </div>
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div className="sect-header">
      <h1>{children}</h1>
      <div className="sect-mark" />
    </div>
  );
}

function PlanCard({ plan }) {
  const features = (plan.items || []).filter(i => i.kind === "feature");
  const addons   = (plan.items || []).filter(i => i.kind === "add_on");
  // Custom lines are charged on top of the package base price.
  const lines    = (plan.items || []).filter(i => i.kind === "line");
  const lineAmt  = (it) => Number(it.quantity || 1) * Number(it.price || 0);
  const subTotal = Number(plan.base_price || 0) + lines.reduce((s, it) => s + lineAmt(it), 0);
  const perUnit  = Number(plan.unit_count) > 0 && Number(plan.unit_price) > 0;
  const unit     = plan.unit_label || "Unit";
  const monthly  = Number(plan.billing_months) === 12;
  return (
    <div className={`plan-card ${plan.is_recommended ? "recommended" : ""}`}>
      {plan.is_recommended && <div className="rec-ribbon">Recommended</div>}
      <div className="plan-head">
        <p className="plan-label">{plan.plan_label}</p>
        <p className="plan-title">{plan.package_title}</p>
        <div className="plan-meta">
          {perUnit && <span>{unit}s: <b>{Number(plan.unit_count).toLocaleString("en-IN")}</b></span>}
          {!perUnit && plan.pricing_product !== "cms" && plan.max_beds > 0 && (
            <span>Beds: <b>{plan.max_beds}</b></span>
          )}
          {!perUnit && (plan.pricing_op_per_year > 0 ? (
            <span>OP visits/yr: <b>{Number(plan.pricing_op_per_year).toLocaleString("en-IN")}</b></span>
          ) : (
            plan.transaction_count_per_year > 0 && (
              <span>Txn/yr: <b>{Number(plan.transaction_count_per_year).toLocaleString("en-IN")}</b></span>
            )
          ))}
        </div>
      </div>

      <div className="plan-body">
        {features.length > 0 && (
          <ul className="plan-features">
            {features.map(f => <li key={f.id || f.name}>{f.name}</li>)}
          </ul>
        )}

        <Locked>
          {perUnit && (
            <table className="unit-table">
              <thead>
                <tr><th>Cost / {unit}</th><th>No. of {unit}s</th><th className="num">Amount{monthly ? " / month" : ""}</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>₹{fmt(plan.unit_price)}</td>
                  <td>{Number(plan.unit_count).toLocaleString("en-IN")}</td>
                  <td className="num">₹{fmt(Number(plan.unit_price) * Number(plan.unit_count))}</td>
                </tr>
              </tbody>
            </table>
          )}
          <div className="plan-totals">
            <Row l={perUnit && monthly ? "Net Amount / Year" : "Base"} v={`₹${fmt(plan.base_price)}`} />
            {lines.map((it, i) => (
              <div key={it.id || i} className="row custom-line">
                <span>
                  {it.name}
                  {Number(it.quantity || 1) !== 1 && (
                    <span className="line-qty"> ({Number(it.quantity).toLocaleString("en-IN")} × ₹{fmt(it.price)})</span>
                  )}
                  {it.description && <span className="line-qty"> — {it.description}</span>}
                </span>
                <span>₹{fmt(lineAmt(it))}</span>
              </div>
            ))}
            {lines.length > 0 && <Row l={<b>Sub-total</b>} v={<b>₹{fmt(subTotal)}</b>} />}
            {Number(plan.discount_amount) > 0 && <Row l="Discount" v={`- ₹${fmt(plan.discount_amount)}`} muted />}
            {Number(plan.discount_amount) > 0 && <Row l="Net" v={`₹${fmt(plan.net_amount)}`} />}
            <Row l={`GST (${Number(plan.gst_rate)}%)`} v={`₹${fmt(plan.gst_amount)}`} muted />
            <div className="plan-total">
              <span>{perUnit && monthly ? "Total Amount / Year" : "Total"}</span><span>₹{fmt(plan.grand_total)}</span>
            </div>
            {Number(plan.amc_rate) > 0 && <p className="amc-note">AMC after year 1: <b>{Number(plan.amc_rate)}%</b></p>}
          </div>
        </Locked>

        {addons.length > 0 && (
          <div className="plan-addons">
            <p className="addon-head">Other Charges &amp; Add-ons</p>
            <Locked>
              <ul>
                {addons.map(a => (
                  <li key={a.id || a.name}>
                    <span>{a.name}{a.description ? <span className="muted"> — {a.description}</span> : null}</span>
                    <span className="addon-price">
                      {Number(a.price) > 0 ? `₹${fmt(a.price)}${a.unit_label ? ` ${a.unit_label}` : ""}` : (a.unit_label || "—")}
                    </span>
                  </li>
                ))}
              </ul>
            </Locked>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ l, v, muted }) {
  return (
    <div className={`row ${muted ? "muted" : ""}`}>
      <span>{l}</span><span>{v}</span>
    </div>
  );
}

const PRINT_STYLES = `
.quotation-doc { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1f2937; }
.quotation-doc .page {
  width: 210mm; min-height: 297mm; background: #fff; margin: 16px auto;
  padding: 20mm 18mm 14mm; position: relative; box-shadow: 0 2px 10px rgba(0,0,0,.08);
  page-break-after: always; break-after: page; overflow: hidden;
}
.quotation-doc .page:last-child {
  page-break-after: auto; break-after: auto;
}
.quotation-doc .page-inner { min-height: calc(297mm - 48mm); }
.quotation-doc .page-footer {
  position: absolute; bottom: 6mm; left: 18mm; right: 18mm;
  display: flex; justify-content: space-between;
  font-size: 10px; color: #94a3b8; border-top: 1px solid #e5e7eb; padding-top: 4mm;
}
.quotation-doc .watermark {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-30deg);
  font-size: 140px; font-weight: 900; letter-spacing: 8px; opacity: .08; pointer-events: none;
  z-index: 0;
}
.quotation-doc .wm-draft    { color: #94a3b8; }
.quotation-doc .wm-accepted { color: var(--accent); }
.quotation-doc .wm-rejected { color: #ef4444; opacity: .1; }
.quotation-doc .wm-expired  { color: #f59e0b; opacity: .1; }

.quotation-doc .cover-header { display: flex; justify-content: space-between; align-items: center; }
.quotation-doc .logo-block { display: flex; align-items: center; gap: 8px; }
.quotation-doc .logo-dot { width: 36px; height: 36px; border-radius: 8px; background: var(--accent); color: #fff; display: grid; place-items: center; font-weight: 700; font-size: 24px; }
.quotation-doc .logo-text { font-size: 18px; font-weight: 700; color: var(--accent); }
.quotation-doc .tn-logo { font-size: 16px; font-weight: 700; color: #1e293b; }
.quotation-doc .cover-title { font-size: 44px; font-weight: 800; line-height: 1.1; margin: 60px 0 16px; color: #0f172a; }
.quotation-doc .cover-subtitle { font-size: 16px; color: #475569; max-width: 520px; }
.quotation-doc .cert-row { display: flex; gap: 10px; margin-top: 36px; flex-wrap: wrap; }
.quotation-doc .cert-pill { border: 1px solid #e5e7eb; padding: 6px 12px; border-radius: 999px; font-size: 11px; color: #64748b; }
.quotation-doc .cover-footer { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 120px; }
.quotation-doc .cover-footer .lbl { font-size: 11px; color: #94a3b8; font-style: italic; }
.quotation-doc .cover-footer h3 { font-size: 20px; margin: 4px 0 2px; color: #0f172a; }
.quotation-doc .muted { color: #94a3b8; }
.quotation-doc .small { font-size: 11px; }

.quotation-doc .sect-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; }
.quotation-doc .sect-header h1 { font-size: 30px; color: #0f172a; margin: 0; }
.quotation-doc .sect-mark { width: 24px; height: 24px; background: var(--accent); border-radius: 3px; }
.quotation-doc .h2 { font-size: 22px; color: #0f172a; margin: 28px 0 12px; }
.quotation-doc .body-text { font-size: 12.5px; line-height: 1.6; color: #334155; }
.quotation-doc .pre-line { white-space: pre-line; }
.quotation-doc .signature { margin-top: 18px; font-size: 13px; color: #0f172a; }

.quotation-doc .two-col { display: grid; grid-template-columns: 1.2fr 1fr; gap: 24px; margin-top: 14px; }
.quotation-doc .why-col { display: grid; gap: 14px; }
.quotation-doc .why-item { display: flex; gap: 10px; }
.quotation-doc .why-dot { width: 28px; height: 28px; background: #0f172a; border-radius: 4px; flex-shrink: 0; }
.quotation-doc .why-title { font-weight: 700; font-size: 13px; color: #0f172a; margin: 0 0 2px; }
.quotation-doc .why-desc { font-size: 11.5px; color: #64748b; margin: 0; }
.quotation-doc .cert-col { background: var(--accent); color: #fff; padding: 14px; border-radius: 8px; }
.quotation-doc .cert-head { font-size: 10px; letter-spacing: 1px; margin: 0 0 12px; opacity: .75; }
.quotation-doc .cert-item { display: flex; gap: 10px; margin-bottom: 10px; }
.quotation-doc .cert-icon { width: 22px; height: 22px; background: #fff; color: var(--accent); border-radius: 4px; display: grid; place-items: center; font-size: 14px; flex-shrink: 0; }
.quotation-doc .cert-name { font-weight: 700; font-size: 12.5px; margin: 0; }
.quotation-doc .cert-desc { font-size: 10.5px; opacity: .8; margin: 0; }

.quotation-doc .what-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 14px; }
.quotation-doc .what-card { background: color-mix(in srgb, var(--accent) 7%, white); border: 1px solid color-mix(in srgb, var(--accent) 35%, white); padding: 14px; border-radius: 8px; }
.quotation-doc .what-card h4 { font-size: 12.5px; margin: 0 0 6px; color: var(--accent); }
.quotation-doc .what-card p { font-size: 11px; margin: 0; color: #475569; }

.quotation-doc .key-list { list-style: none; padding: 0; margin-top: 18px; }
.quotation-doc .key-list li { padding: 10px 0; border-bottom: 1px dashed #e5e7eb; font-size: 13px; }
.quotation-doc .key-list li::before { content: '❖'; color: var(--accent); margin-right: 10px; }

.quotation-doc .prod-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 14px; }
.quotation-doc .prod-card { background: #f8fafc; border: 1px solid #e5e7eb; padding: 10px; border-radius: 6px; display: flex; align-items: center; gap: 8px; font-size: 11.5px; font-weight: 600; color: #1e293b; }
.quotation-doc .prod-ico { width: 22px; height: 22px; background: var(--accent); color: #fff; border-radius: 4px; display: grid; place-items: center; font-size: 12px; }

.quotation-doc .plans-row { display: grid; gap: 12px; margin-top: 14px; }
.quotation-doc .plans-1 { grid-template-columns: 1fr; }
.quotation-doc .plans-2 { grid-template-columns: 1fr 1fr; }
.quotation-doc .plans-3 { grid-template-columns: 1fr 1fr 1fr; }
.quotation-doc .plan-card { border: 1.5px solid #e5e7eb; border-radius: 10px; overflow: hidden; background: #fff; position: relative; }
.quotation-doc .plan-card.recommended { border-color: var(--accent); box-shadow: 0 2px 8px color-mix(in srgb, var(--accent) 25%, transparent); }
.quotation-doc .rec-ribbon { position: absolute; top: 0; right: 0; background: var(--accent); color: #fff; padding: 3px 10px; font-size: 10px; font-weight: 700; border-radius: 0 10px 0 10px; }
.quotation-doc .approval-stamp { display: inline-block; margin: 10px 0; padding: 6px 14px; font-size: 12px; font-weight: 800; letter-spacing: 1.5px; border-radius: 4px; border: 2px solid; transform: rotate(-2deg); }
.quotation-doc .stamp-pending  { color: #a16207; border-color: #a16207; background: rgba(250,204,21,.15); }
.quotation-doc .stamp-approved { color: #15803d; border-color: #15803d; background: rgba(34,197,94,.10); }
.quotation-doc .stamp-rejected { color: #b91c1c; border-color: #b91c1c; background: rgba(239,68,68,.12); }
.quotation-doc .plan-head { padding: 12px; background: color-mix(in srgb, var(--accent) 7%, white); border-bottom: 1px solid #e5e7eb; }
.quotation-doc .plan-label { font-size: 10px; letter-spacing: 1px; color: var(--accent); font-weight: 700; margin: 0; }
.quotation-doc .plan-title { font-size: 15px; font-weight: 700; color: #0f172a; margin: 2px 0 8px; }
.quotation-doc .plan-meta { display: flex; gap: 12px; font-size: 10.5px; color: #475569; }
.quotation-doc .plan-body { padding: 10px 12px; }
.quotation-doc .plan-features { list-style: none; padding: 0; margin: 0 0 10px; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; }
.quotation-doc .plan-features li { font-size: 11px; padding: 2px 0; color: #334155; }
.quotation-doc .plan-features li::before { content: '▸ '; color: var(--accent); }
.quotation-doc .plan-totals { font-size: 11px; color: #334155; }
.quotation-doc .row { display: flex; justify-content: space-between; padding: 2px 0; }
.quotation-doc .row.muted { color: #94a3b8; }
.quotation-doc .plan-total { display: flex; justify-content: space-between; margin-top: 6px; padding-top: 6px; border-top: 1.5px solid #0f172a; font-size: 13px; font-weight: 700; color: #0f172a; }
.quotation-doc .amc-note { font-size: 10px; color: #64748b; margin: 6px 0 0; }
.quotation-doc .plan-addons { margin-top: 10px; padding-top: 8px; border-top: 1px dashed #e5e7eb; }
.quotation-doc .addon-head { font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 4px; }
.quotation-doc .plan-addons ul { list-style: none; padding: 0; margin: 0; }
.quotation-doc .plan-addons li { display: flex; justify-content: space-between; font-size: 10.5px; padding: 2px 0; }
.quotation-doc .addon-price { color: var(--accent); font-weight: 600; }
.quotation-doc .row.custom-line { gap: 8px; }
.quotation-doc .row.custom-line > span:last-child { white-space: nowrap; }
.quotation-doc .line-qty { font-size: 10px; color: #64748b; }


.quotation-doc .other-charges { margin-top: 16px; background: #fffbeb; border-left: 3px solid #f59e0b; padding: 10px 14px; border-radius: 4px; }
.quotation-doc .other-head { font-weight: 700; font-size: 12px; color: #92400e; margin: 0 0 6px; }

.quotation-doc .warn { background: #fef2f2; border-left: 3px solid #dc2626; padding: 10px; font-size: 11.5px; color: #991b1b; font-style: italic; border-radius: 4px; }
.quotation-doc .pay-row { display: grid; grid-template-columns: 2fr 1fr; gap: 14px; margin-top: 14px; }
.quotation-doc .pay-box { border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px; }
.quotation-doc .pay-head { font-weight: 700; font-size: 13px; color: #0f172a; margin: 0 0 8px; }
.quotation-doc .pay-list { list-style: none; padding: 0; margin: 0; }
.quotation-doc .pay-list li { font-size: 11.5px; padding: 3px 0; color: #334155; }
.quotation-doc .qr-box { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }

/* Print media */
@media print {
  html, body { background: #fff; margin: 0; padding: 0; }
  .no-print { display: none !important; }
  .quotation-doc {
    /* Ensure no ancestor constrains page flow */
    display: block; width: auto; height: auto;
  }
  .quotation-doc .page {
    margin: 0; box-shadow: none;
    /* Let @page handle the physical page size; don't force 297mm here
       (hard min-height + overflow:hidden was truncating multi-page flow
       in Chrome/Edge "Save as PDF" and collapsing to a single page). */
    width: 210mm; min-height: 0; height: auto;
    overflow: visible;
    page-break-after: always; break-after: page;
    page-break-inside: avoid;
  }
  .quotation-doc .page:last-child {
    page-break-after: auto; break-after: auto;
  }
  /* Avoid splitting these inline blocks across pages */
  .quotation-doc .plan-card,
  .quotation-doc .what-card,
  .quotation-doc .why-item,
  .quotation-doc .cert-item,
  .quotation-doc .pay-box { page-break-inside: avoid; break-inside: avoid; }
}

.quotation-doc .unit-table, .quotation-doc .sum-table { width: 100%; border-collapse: collapse; font-size: 11px; margin: 4px 0 8px; }
.quotation-doc .unit-table th, .quotation-doc .sum-table th { text-align: left; font-weight: 600; color: #fff; background: var(--accent); padding: 5px 6px; }
.quotation-doc .unit-table td, .quotation-doc .sum-table td { padding: 5px 6px; border-bottom: 1px solid #e5e7eb; color: #334155; vertical-align: top; }
.quotation-doc .sum-table { font-size: 12px; margin-top: 14px; }
.quotation-doc .sum-table tfoot td { font-weight: 700; color: #0f172a; border-top: 1.5px solid #0f172a; border-bottom: none; padding-top: 8px; }
.quotation-doc .num { text-align: right; white-space: nowrap; }

/* Edit-before-print: show what can be typed over and what is locked. */
.quotation-doc.is-editing .page-inner { outline: 1px dashed #93c5fd; outline-offset: 4px; }
.quotation-doc.is-editing .locked { outline: 1px dashed #fca5a5; outline-offset: 2px; cursor: not-allowed; }
.quotation-doc.is-editing [contenteditable="true"]:focus { outline: none; }
@media print {
  .quotation-doc.is-editing .page-inner, .quotation-doc.is-editing .locked { outline: none; }
}

@page { size: A4; margin: 0; }
`;
